import { ChatButton } from '@chewy/chat-library-components';
import {
  ActionDef,
  ChatWrapupSheetActionType,
  useConversation,
} from '@chewy/chat-library-common';
import styles from './ChatWrapupCard.module.scss';
import {
  FEEDBACK_SUBMIT_THANK_YOU_TITLE_FALLBACK,
  getFeedbackSubmitThankYouDescription,
} from './chatWrapupFeedbackThankYouCopy';
import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css';
import { KibThumbupOutlineIcon, KibThumbdownOutlineIcon } from '@chewy/kib-icons-react';
import { KibButtonNew } from '@chewy/kib-controls-react';

import { useMemo, useState } from 'react';

interface ChatWrapupCardData {
  title?: string;
  includeFeedback?: boolean;
  feedbackEmail?: string;
  onSendFeedback?: (positive: boolean, comments?: string) => Promise<void>;
  onSendEmailChatTranscript?: (email?: string) => Promise<void>;
  onCloseChat?: () => void;
  onStartChat?: () => void;
}

interface ChatWrapupCardProps {
  data: ChatWrapupCardData[];
}

const FEEDBACK_ACTIONS: Record<string, ActionDef> = {
  positive: {
    type: 'button',
    ctaType: 'command',
    text: 'Yes',
    payload: { action: ChatWrapupSheetActionType.CHAT_FEEDBACK_POSITIVE },
  },
  negative: {
    type: 'button',
    ctaType: 'command',
    text: 'No',
    payload: { action: ChatWrapupSheetActionType.CHAT_FEEDBACK_NEGATIVE, comments: '' },
  }
};

const WRAPUP_CARD_TITLE_FALLBACK =
  `Did that help? I'm always learning and your feedback helps.`;

export default function ChatWrapupCard({ data }: ChatWrapupCardProps) {
  const actualData = Array.isArray(data) ? data[0] : data;
  const {
    wrapupFeedbackThankYou,
    clearWrapupFeedbackThankYou,
  } = useConversation();
  const includeFeedback = useMemo(() => actualData.includeFeedback ?? true, [actualData.includeFeedback]);
  const feedbackEmail = useMemo(() => actualData.feedbackEmail, [actualData.feedbackEmail]);
  const copyText = 'Do you want a copy of this chat for future reference? We can ';
  const copyLinkText = 'email you a copy';
  const [selectedFeedbackType, setSelectedFeedbackType] = useState<ActionDef>();

  const feedbackDescription = useMemo(() => {
    if (selectedFeedbackType === FEEDBACK_ACTIONS.negative) {
      return "What could I have done better? I'd love to know more so I can improve.";
    }
    if (selectedFeedbackType === FEEDBACK_ACTIONS.positive) {
      return "What did you like? I'd love to know more so I can do more of it.";
    }
    return '';
  }, [selectedFeedbackType]);

  const thankYouState = wrapupFeedbackThankYou ?? null;
  /** Widget/CMS title whenever Yes/No or pre-submit prompts show; thank-you title after successful sheet submit. */
  const displayTitle = thankYouState
    ? FEEDBACK_SUBMIT_THANK_YOU_TITLE_FALLBACK
    : (actualData.title ?? WRAPUP_CARD_TITLE_FALLBACK);
  const displayBodyCopy = thankYouState
    ? getFeedbackSubmitThankYouDescription(thankYouState.sentiment)
    : feedbackDescription;

  const handleFeedbackAction = async (action: ActionDef) => {
    clearWrapupFeedbackThankYou?.();
    if (action.payload.action === 'CHAT_FEEDBACK_NEGATIVE') {
      try {
        await actualData.onSendFeedback?.(false);
        setSelectedFeedbackType(FEEDBACK_ACTIONS.negative);
      } catch (error) {
        console.error(error);
      }

    } else {
      try {
        await actualData.onSendFeedback?.(true);
        setSelectedFeedbackType(FEEDBACK_ACTIONS.positive);
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleCopyLinkAction = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    try {
      await actualData.onSendEmailChatTranscript?.();
    } catch (error) {
      console.error(error);
    }
  };

  const handleCloseChatAction = () => {
    try {
      actualData.onCloseChat?.();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className={styles.container}>

      {includeFeedback && (
      <p className={styles.title}>{displayTitle}</p>
      )}

      {includeFeedback && !selectedFeedbackType && (
      <div className={styles.feedbackButtons}>
        <ChatButton
          className={styles.feedbackButton}
          onClick={() => handleFeedbackAction(FEEDBACK_ACTIONS.positive)}
          icon={<KibThumbupOutlineIcon />}
          buttonText={FEEDBACK_ACTIONS.positive.text}
        />
          <ChatButton
          className={styles.feedbackButton}
            onClick={() => handleFeedbackAction(FEEDBACK_ACTIONS.negative)}
            icon={<KibThumbdownOutlineIcon />}
            buttonText={FEEDBACK_ACTIONS.negative.text}
          />
          </div>
          )}

      {includeFeedback && selectedFeedbackType && displayBodyCopy ? (
      <p
      className={
        thankYouState ? styles.feedbackThankYouBody : styles.copyText
        }
        >
        {displayBodyCopy}
        </p>
        ) : null}



        {feedbackEmail ? (
          <p className={styles.copyText}>
            {`We’ll send a copy of this chat to ${feedbackEmail}. It might take a few minutes to get to your inbox.`}
          </p>
        ) : (
          <p className={styles.copyText}>
            {copyText}
            <a
              href={'#'}
              className={styles.copyLink}
              onClick={handleCopyLinkAction}
            >
              {copyLinkText}
            </a>
          </p>
        )}
        <div className={styles.actionWrapper}>
          <KibButtonNew
            onClick={handleCloseChatAction}
            theme="action"
            emphasis="secondary"
            size="medium"
            className={styles.actionButton}
          >
            {`Close chat`}
          </KibButtonNew>
        </div>
    </div>
  );
}
